
import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Link, Star } from "lucide-react";

const ReviewLinks = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const defaultLinks = {
    google: "",
    facebook: "",
    sprintmint: "",
    custom: {
      name: "",
      url: ""
    }
  };
  const [reviewLinks, setReviewLinks] = useState(defaultLinks);
  const [primaryReviewLink, setPrimaryReviewLink] = useState<string>("");
  const [businessName, setBusinessName] = useState("");
  const [baseline, setBaseline] = useState({ links: defaultLinks, primary: "" });

  const hasChanges =
    JSON.stringify(reviewLinks) !== JSON.stringify(baseline.links) ||
    primaryReviewLink !== baseline.primary;

  useEffect(() => {
    if (user) {
      fetchProfileData();
    }
  }, [user]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      if (!user) return;

      // Get profile data
      const { data, error } = await supabase
        .from('profiles')
        .select('review_links, business_name, primary_review_link')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        toast.error(`Error fetching profile: ${error.message}`);
        return;
      }

      console.log('Profile data:', data);

      // Set business name if available
      if (data?.business_name) {
        setBusinessName(data.business_name);
      }

      const nextPrimary = data?.primary_review_link || "";
      setPrimaryReviewLink(nextPrimary);

      // Prepare review links (ensure it's an object with the correct structure)
      let links = {
        google: "",
        facebook: "",
        sprintmint: "",
        custom: {
          name: "",
          url: ""
        }
      };

      if (data?.review_links) {
        // Parse JSON string if needed
        const parsedLinks = typeof data.review_links === 'string' ? JSON.parse(data.review_links) : data.review_links;

        // Merge with default structure to ensure type safety
        links = {
          ...links,
          ...(parsedLinks || {}),
          // Handle migration from checkatrade to sprintmint
          sprintmint: parsedLinks?.sprintmint || parsedLinks?.checkatrade || "",
          // Ensure custom object exists with proper structure
          custom: {
            name: parsedLinks?.custom?.name || "",
            url: parsedLinks?.custom?.url || ""
          }
        };
      }
      setReviewLinks(links);
      setBaseline({ links, primary: nextPrimary });
    } catch (error: any) {
      console.error('Error:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const updateProfileData = async () => {
    try {
      setLoading(true);
      if (!user) {
        toast.error('You must be logged in to update your profile');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          review_links: reviewLinks,
          primary_review_link: primaryReviewLink
        })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      toast.success('Review links updated successfully');
      setBaseline({ links: reviewLinks, primary: primaryReviewLink });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(`Failed to update: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle updating review links
  const updateReviewLink = (platform: keyof typeof reviewLinks, value: string) => {
    setReviewLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  // Handle updating custom review link
  const updateCustomReviewLink = (field: 'name' | 'url', value: string) => {
    setReviewLinks(prev => ({
      ...prev,
      custom: {
        ...prev.custom,
        [field]: value
      }
    }));
  };

  // Handle primary review link toggle
  const handlePrimaryToggle = (platform: string, isEnabled: boolean) => {
    if (isEnabled) {
      // Check if the platform has a URL before setting as primary
      const hasUrl = getPlatformUrl(platform) !== "";
      if (hasUrl) {
        setPrimaryReviewLink(platform);
      } else {
        toast.error("Please add a URL for this platform before making it primary");
      }
    } else {
      // If toggling off the current primary, clear it
      if (primaryReviewLink === platform) {
        setPrimaryReviewLink("");
      }
    }
  };

  // Get the URL for a platform
  const getPlatformUrl = (platform: string) => {
    switch (platform) {
      case 'google':
        return reviewLinks.google;
      case 'facebook':
        return reviewLinks.facebook;
      case 'sprintmint':
        return reviewLinks.sprintmint;
      case 'custom':
        return reviewLinks.custom.url;
      default:
        return '';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Review Links</h1>
        <p className="text-muted-foreground mt-2">
          Manage your review links and set your primary review platform
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-mint" />
              <span>Review Links</span>
            </CardTitle>
            <CardDescription>
              Add links where customers can leave reviews for your business
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Google Review Link */}
            <div className="space-y-3">
              <Label htmlFor="google_review">Google Review Link</Label>
              <Input
                id="google_review"
                value={reviewLinks.google}
                onChange={(e) => updateReviewLink('google', e.target.value)}
                placeholder="https://g.page/r/..."
              />
              <div className="flex items-center space-x-2">
                <Switch
                  id="google_primary"
                  checked={primaryReviewLink === 'google'}
                  onCheckedChange={(checked) => handlePrimaryToggle('google', checked)}
                  disabled={!reviewLinks.google}
                />
                <Label htmlFor="google_primary" className="text-sm flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Make Primary Review Link
                </Label>
              </div>
            </div>

            {/* Facebook Review Link */}
            <div className="space-y-3">
              <Label htmlFor="facebook_review">Facebook Review Link</Label>
              <Input
                id="facebook_review"
                value={reviewLinks.facebook}
                onChange={(e) => updateReviewLink('facebook', e.target.value)}
                placeholder="https://facebook.com/..."
              />
              <div className="flex items-center space-x-2">
                <Switch
                  id="facebook_primary"
                  checked={primaryReviewLink === 'facebook'}
                  onCheckedChange={(checked) => handlePrimaryToggle('facebook', checked)}
                  disabled={!reviewLinks.facebook}
                />
                <Label htmlFor="facebook_primary" className="text-sm flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Make Primary Review Link
                </Label>
              </div>
            </div>

            {/* Custom Review Link */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-base font-medium">Custom Review Link</Label>
              
              <div className="space-y-2">
                <Label htmlFor="custom_review_name" className="text-sm">Link Name</Label>
                <Input
                  id="custom_review_name"
                  value={reviewLinks.custom.name}
                  onChange={(e) => updateCustomReviewLink('name', e.target.value)}
                  placeholder="e.g., Trustpilot, Yelp, etc."
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="custom_review_url" className="text-sm">Link URL</Label>
                <Input
                  id="custom_review_url"
                  value={reviewLinks.custom.url}
                  onChange={(e) => updateCustomReviewLink('url', e.target.value)}
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="custom_primary"
                  checked={primaryReviewLink === 'custom'}
                  onCheckedChange={(checked) => handlePrimaryToggle('custom', checked)}
                  disabled={!reviewLinks.custom.url || !reviewLinks.custom.name}
                />
                <Label htmlFor="custom_primary" className="text-sm flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Make Primary Review Link
                </Label>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Add any other review platform you use
              </p>
            </div>

            {primaryReviewLink && (
              <div className="mt-4 p-3 bg-mint/10 rounded-lg border border-mint/20">
                <div className="flex items-center gap-2 text-sm font-medium text-mint">
                  <Star className="h-4 w-4 fill-current" />
                  Primary Review Link Set
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {primaryReviewLink === 'custom' 
                    ? reviewLinks.custom.name 
                    : primaryReviewLink.charAt(0).toUpperCase() + primaryReviewLink.slice(1)}{' '}
                  will be automatically selected on the dashboard
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button onClick={updateProfileData} disabled={loading || !hasChanges}>
              {loading ? 'Saving...' : 'Save Review Links'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ReviewLinks;
